import React from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorIcon from '@mui/icons-material/Error';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';

import type { BulkClaimResult } from '~/types/loyaltyContract';

import { downloadFailedClaimsCsv } from '../csvHelpers';

interface Props {
  result: BulkClaimResult;
  onStartNew: () => void;
}

export default function ResultStep({ result, onStartNew }: Props) {
  const { successCount, failedCount, results } = result;
  const hasFailures = failedCount > 0;
  const failedResults = results.filter((r) => !r.success);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" py={4}>
      {hasFailures ? (
        <ErrorIcon color="warning" sx={{ fontSize: 64, mb: 2 }} />
      ) : (
        <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
      )}

      <Typography variant="h5" gutterBottom>
        {hasFailures ? 'Redemptions Processed with Errors' : 'Redemptions Processed Successfully'}
      </Typography>

      <Stack direction="row" spacing={4} mt={2} mb={3}>
        <Box textAlign="center">
          <Typography variant="h4" color="success.main">
            {successCount}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Succeeded
          </Typography>
        </Box>
        {hasFailures && (
          <Box textAlign="center">
            <Typography variant="h4" color="error.main">
              {failedCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Failed
            </Typography>
          </Box>
        )}
      </Stack>

      {hasFailures && (
        <Alert severity="warning" sx={{ mb: 3, maxWidth: 600, width: '100%' }}>
          <Typography variant="body2">
            {failedCount} redemption(s) could not be processed. Download the error report for details.
          </Typography>
          <Typography variant="caption" color="text.secondary" mt={1} display="block">
            For server errors (status code 500), please contact the Loyalty team to investigate and resolve.
          </Typography>
        </Alert>
      )}

      <Stack direction="row" spacing={2}>
        {hasFailures && failedResults.length > 0 && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => downloadFailedClaimsCsv(failedResults)}
          >
            Download Error Report
          </Button>
        )}
        <Button variant="contained" onClick={onStartNew}>
          Start New Redemption
        </Button>
      </Stack>
    </Box>
  );
}
